'use client';

import { useState, useEffect, Suspense, type CSSProperties } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/lib/contexts/AuthContext';
import { supabase } from '@/lib/integrations/supabase/client';
import { toast } from 'sonner';
import { toastError, cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Checkbox } from '@/components/ui/checkbox';
import { registerSchema, type RegisterInput } from '@/lib/validation/registerSchema';
import { Copyright } from '@/components/Copyright';
import { softenGradient, generateThemeStyles } from '@/lib/constants/backgrounds';
import { readCachedBrand, clearCachedBrand, type CachedBrand } from '@/lib/utils/brandCache';
import { maskEmail } from '@/lib/utils/maskEmail';

export default function Login() {
  return (
    <Suspense>
      <LoginInner />
    </Suspense>
  );
}

function LoginInner() {
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<'login' | 'register'>(
    searchParams.get('mode') === 'register' ? 'register' : 'login'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { user, signIn, isLoading: isAuthLoading } = useAuth();
  const router = useRouter();

  // Greet the user with their last school's branding, cached to localStorage
  // while they were signed in. Read in an effect so SSR/first paint stays neutral.
  const [brand, setBrand] = useState<CachedBrand | null>(null);
  useEffect(() => {
    const b = readCachedBrand();
    setBrand(b);
    // Pre-fill the real email (shown masked) so a returning user only types their password
    if (b?.userEmail) setEmail(b.userEmail);
  }, []);
  const resetBrand = () => { clearCachedBrand(); setBrand(null); setEmail(''); };

  // Same softened gradient the hubs use — the login is the normal background, not a louder version
  const brandBg: CSSProperties | undefined = brand?.gradient
    ? { background: softenGradient(brand.gradient) }
    : brand?.themeColor
      ? generateThemeStyles(brand.themeColor).bgStyle
      : undefined;
  const branded = !!(brand && (brand.gradient || brand.themeColor || brand.logoUrl));
  // A recognized returning user: greet by name, mask their email, drop the sign-up CTA
  const greetName = brand?.userName ?? null;
  const returning = branded || !!greetName;

  // Store referral code + referrer name from URL so it persists through signup → checkout
  const refParam = searchParams.get('ref');
  const [hasReferral, setHasReferral] = useState(false);
  useEffect(() => {
    if (refParam) {
      localStorage.setItem('referral_code', refParam);
      setHasReferral(true);
      // Look up referrer name for display
      fetch(`/api/referral/lookup?code=${encodeURIComponent(refParam)}`)
        .then(r => r.json())
        .then(d => { if (d.name) localStorage.setItem('referrer_name', d.name); })
        .catch(() => {});
    } else if (localStorage.getItem('referral_code')) {
      setHasReferral(true);
    }
  }, [refParam]);

  const {
    register,
    handleSubmit: handleRegisterSubmit,
    formState: { errors },
    reset: resetRegisterForm,
    control,
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: '', confirmEmail: '', password: '', confirmPassword: '', acknowledgements: false as unknown as true },
  });

  // Redirect to home if already logged in
  useEffect(() => {
    if (!isAuthLoading && user) {
      router.replace('/');
    }
  }, [user, isAuthLoading, router]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await signIn(email, password);
      toast.success('Logged in successfully!');
      setIsLoading(false);
      setTimeout(() => {
        router.push('/');
      }, 500);
    } catch (error) {
      console.error('Auth error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred';
      toast.error(message);
      setIsLoading(false);
    }
  };

  // OAuth lands on /auth/callback, which provisions brand-new users and gates them on Terms acceptance.
  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
    } catch (error) {
      toastError(error, 'Google sign-in failed');
    }
  };

  const onRegister = async (data: RegisterInput) => {
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          confirmEmail: data.confirmEmail,
          password: data.password,
          confirmPassword: data.confirmPassword,
          acknowledgements: data.acknowledgements,
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to create account');
      }

      await signIn(data.email, data.password);
      toast.success('Account created successfully!');
      setTimeout(() => {
        router.push('/');
      }, 500);
    } catch (error) {
      console.error('Registration error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred';
      toast.error(message);
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setEmail('');
    setPassword('');
    resetRegisterForm();
    setIsLoading(false);
  };

  return (
    <div
      className={cn(
        'min-h-screen flex flex-col relative overflow-hidden',
        !brandBg && 'bg-gradient-to-br from-yellow-50 via-orange-50 to-purple-100',
      )}
      style={brandBg}
    >
      {/* Decorative background elements (default theme only) */}
      {!branded && (
        <>
          <div className="absolute top-20 -right-20 w-96 h-96 bg-red-200/30 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-20 -left-20 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl pointer-events-none" />
        </>
      )}

      {/* Logo — the school's when we know it, else Admin4You; sits where the Admin4You logo always has */}
      <div className="flex justify-center pt-10 pb-6 z-10 sm:absolute sm:top-8 sm:left-8 sm:pt-0 sm:pb-0">
        <img
          src={branded && brand?.logoUrl ? brand.logoUrl : '/a4uLogo.png'}
          alt={branded && brand?.name ? brand.name : 'Admin4You'}
          className="h-16 w-auto sm:h-20 max-h-20 object-contain"
        />
      </div>

      {/* Centered form */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 relative z-10">
        <div className="w-full max-w-[450px]">
          {/* Referral discount banner */}
          {hasReferral && (
            <div className="mb-6 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-center">
              <p className="text-sm font-medium text-emerald-800">
                You&apos;ve been referred! Sign up and get <span className="font-bold">20% off</span> your first month.
              </p>
            </div>
          )}

          {/* Script heading */}
          <h1 className="text-4xl font-thin text-gray-900 mb-8 sm:text-6xl sm:mb-12" style={{ fontFamily: 'Alex Brush, cursive' }}>
            {mode === 'register'
              ? 'Join Us Today'
              : greetName
                ? `Welcome back, ${greetName}`
                : 'Welcome Back'}
          </h1>

          {mode === 'login' ? (
            <form key="login" onSubmit={handleLogin} className="space-y-6">
              {returning && brand?.userEmail ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                    {maskEmail(brand.userEmail)}
                  </div>
                </div>
              ) : (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Link href="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-primary via-primary to-secondary disabled:opacity-50 text-white font-semibold text-base rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl bg-[length:200%_100%] bg-left-top"
                style={{
                  backgroundImage: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--secondary)), hsl(var(--primary)))'
                }}
                onMouseEnter={(e) => e.currentTarget.style.animation = 'gradient-shift 3s ease-in-out infinite'}
                onMouseLeave={(e) => e.currentTarget.style.animation = 'none'}
                onClick={(e) => e.currentTarget.style.animation = 'gradient-shift 0.4s ease-in-out'}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full py-3 px-4 flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-700 font-medium text-base rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.22V7.04H2.18a11 11 0 0 0 0 9.92l3.66-2.86z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                </svg>
                Continue with Google
              </button>

              {/* Returning users (recognized brand/name) don't need a sign-up CTA */}
              {!returning && (
                <div className="text-center pt-2">
                  <p className="text-sm text-gray-500 mb-3">Don&apos;t have an account?</p>
                  <button
                    type="button"
                    onClick={switchMode}
                    className="w-full py-3 px-4 border-2 border-primary text-primary font-semibold text-base rounded-lg hover:bg-primary/5 transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </form>
          ) : (
            <form key="register" onSubmit={handleRegisterSubmit(onRegister)} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <Input
                  id="name"
                  type="text"
                  {...register('name')}
                  placeholder="Jane Smith"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <Input
                  id="reg-email"
                  type="email"
                  {...register('email')}
                  placeholder="jane@school.edu"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="reg-confirm-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Email
                </label>
                <Input
                  id="reg-confirm-email"
                  type="email"
                  {...register('confirmEmail')}
                  placeholder="jane@school.edu"
                  onPaste={(e) => e.preventDefault()}
                />
                {errors.confirmEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmEmail.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <PasswordInput
                  id="reg-password"
                  {...register('password')}
                />
                {errors.password ? (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Min 8 chars, uppercase, lowercase, number, and special character
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-start gap-2">
                  <Controller
                    control={control}
                    name="acknowledgements"
                    render={({ field }) => (
                      <Checkbox
                        id="acknowledgements"
                        checked={!!field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                        className="mt-0.5"
                      />
                    )}
                  />
                  <label htmlFor="acknowledgements" className="text-sm text-gray-700 select-none">
                    I have read and agree to the{' '}
                    <Link
                      href="/terms"
                      target="_blank"
                      className="text-primary font-medium hover:underline"
                    >
                      Terms of Use
                    </Link>{' '}
                    and{' '}
                    <Link
                      href="/privacy"
                      target="_blank"
                      className="text-primary font-medium hover:underline"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </label>
                </div>
                {errors.acknowledgements && (
                  <p className="mt-1 text-sm text-red-600">{errors.acknowledgements.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-primary via-primary to-secondary disabled:opacity-50 text-white font-semibold text-base rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl bg-[length:200%_100%] bg-left-top"
                style={{
                  backgroundImage: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--secondary)), hsl(var(--primary)))'
                }}
                onMouseEnter={(e) => e.currentTarget.style.animation = 'gradient-shift 3s ease-in-out infinite'}
                onMouseLeave={(e) => e.currentTarget.style.animation = 'none'}
                onClick={(e) => e.currentTarget.style.animation = 'gradient-shift 0.4s ease-in-out'}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>

              <div className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button type="button" onClick={switchMode} className="text-primary font-semibold hover:underline">
                  Sign In
                </button>
              </div>
            </form>
          )}

          {/* Returning user: let a different person drop the cached greeting/branding */}
          {returning && mode === 'login' && (
            <p className="mt-6 text-center text-xs text-gray-500">
              <button type="button" onClick={resetBrand} className="underline hover:text-gray-700">
                {greetName ? `Not ${greetName}?` : 'Not you?'}
              </button>
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 px-4 relative z-10 space-y-3">
        {/* The only in-app entry point to /intro. It was deleted once for being unreachable;
            anyone landing on the sign-in screen without knowing what Admin4You is needs it. */}
        <p className="text-center text-sm text-gray-600">
          New to Admin4You?{' '}
          <Link href="/intro" className="text-primary font-medium hover:underline">
            See what it does
          </Link>
        </p>
        <Copyright />
      </div>
    </div>
  );
}
